import { IconEye, IconEyeOff, IconLock } from "@tabler/icons-react"
import {
	type ComponentType,
	type FC,
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useState,
} from "react"

import { exportEditorState, isEditorStateEmpty, loadEditorState } from "@forento/core/utilities/lexical/interactive"
import { type LexicalEditorType } from "@forento/ui/components/LexicalEditor"
import { type FCC } from "@forento/ui/types/react"
import { cn } from "@forento/ui/utilities/tailwind"

import Loader from "~/components/loader"

const LexicalEditor = lazy(() => import("@forento/ui/components/LexicalEditor"))

export const Label: FCC<{ forId?: string }> = ({ forId, children }) => (
	<label htmlFor={forId} className="mb-2 block text-sm font-medium text-slate-700">
		{children}
	</label>
)

type InputProps = {
	type: "text" | "email" | "number"
	label?: string
	value: string
	onChange(value: string): void
	icon?: ComponentType<{ className?: string }>
	placeholder: string
	autoComplete?: string
	autoFocus?: boolean
	required?: boolean
	className?: string
}
export const Input: FC<InputProps> = ({
	type,
	label,
	value,
	onChange,
	placeholder,
	autoComplete,
	autoFocus,
	icon: Icon,
	required,
	className,
}) => {
	const id = useId()

	return (
		<div className={className}>
			{label && <Label forId={id}>{label}</Label>}
			<div className="relative">
				{Icon && <Icon className="absolute top-1/2 left-3 size-5 -translate-y-1/2 text-slate-400" />}
				<input
					id={id}
					type={type}
					value={value}
					onChange={e => onChange(e.target.value)}
					className={cn(
						"focus:border-primary focus:ring-primary w-full rounded-lg border border-slate-300 bg-white py-3 text-sm text-slate-900 placeholder-slate-500 focus:ring-1 focus:outline-none",
						Icon ? "px-10" : "px-4",
					)}
					placeholder={placeholder}
					autoComplete={autoComplete}
					autoFocus={autoFocus}
					required={required}
				/>
			</div>
		</div>
	)
}

type PasswordInputProps = {
	label: string
	value: string
	onChange(value: string): void
	placeholder: string
	autoComplete: string
}
export const PasswordInput: FC<PasswordInputProps> = ({ label, value, onChange, placeholder, autoComplete }) => {
	const id = useId()

	const [showPassword, setShowPassword] = useState(false)

	return (
		<div>
			<Label forId={id}>{label}</Label>
			<div className="relative">
				<IconLock className="absolute top-1/2 left-3 size-5 -translate-y-1/2 text-slate-400" />
				<input
					id={id}
					type={showPassword ? "text" : "password"}
					value={value}
					onChange={e => onChange(e.target.value)}
					required
					className="focus:border-primary focus:ring-primary w-full rounded-lg border border-slate-300 bg-white px-10 py-3 text-sm text-slate-900 placeholder-slate-500 focus:ring-1 focus:outline-none"
					placeholder={placeholder}
					autoComplete={autoComplete}
				/>
				<button
					type="button"
					onClick={() => setShowPassword(!showPassword)}
					className="absolute top-1/2 right-3 -translate-y-1/2 text-slate-400 hover:text-slate-600"
				>
					{showPassword ? <IconEyeOff className="size-5" /> : <IconEye className="size-5" />}
				</button>
			</div>
		</div>
	)
}

type RichTextAreaParams = { label?: string; initialValue?: string | null; extendedFormatting?: boolean }
export function useRichTextArea({ label, initialValue, extendedFormatting }: RichTextAreaParams) {
	const id = useId()
	const [editorRef, setEditorRef] = useState<LexicalEditorType>()
	const [isModified, setModified] = useState(false)

	const element = useMemo(
		() => (
			<div className="flex flex-col">
				{label && <Label forId={id}>{label}</Label>}
				<Suspense fallback={<Loader label="Laddar redigerare..." />}>
					<div
						id={id}
						className="lexical-editor focus:border-primary focus:ring-primary w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:ring-1 focus:outline-none"
					>
						<LexicalEditor editorRef={setEditorRef} extendedFormatting={extendedFormatting} />
					</div>
				</Suspense>
			</div>
		),
		[id, label, extendedFormatting],
	)

	useLayoutEffect(() => {
		if (editorRef && initialValue !== undefined) {
			loadEditorState(editorRef, initialValue)
		}
	}, [initialValue, editorRef])

	useEffect(() => {
		if (!editorRef) return
		const removeListener = editorRef.registerUpdateListener(({ editorState }) => {
			if (initialValue) {
				setModified(JSON.stringify(editorState) !== initialValue)
			} else {
				setModified(!isEditorStateEmpty(editorState))
			}
		})
		return () => {
			removeListener()
		}
	}, [editorRef, initialValue])

	const handleClear = useCallback(() => {
		if (!editorRef) return
		loadEditorState(editorRef, null)
	}, [editorRef])

	const handleSetUnmodified = useCallback(() => {
		setModified(false)
	}, [])

	return useMemo(
		() => ({
			element,
			clear: handleClear,
			exportEditorState() {
				if (!editorRef) return null
				return exportEditorState(editorRef.getEditorState())
			},
			setUnmodified: handleSetUnmodified,
			isModified,
		}),
		[editorRef, element, handleClear, handleSetUnmodified, isModified],
	)
}
